/**
 * Shared delivery + failure handling for POST-based report exports. See PS-9885.
 *
 * Exports used to be a plain GET navigation, so the browser handled delivery
 * for free: it read `Content-Disposition` and saved CSV/XLSX (`attachment`)
 * while rendering PDF inline (Snappy's `stream()` sends `inline`). A failure at
 * least painted an error page in the new tab.
 *
 * Moving the filter payload into a POST body means fetching the bytes as a blob,
 * so the response never reaches the browser's navigation layer and that header
 * becomes inert. Delivery and failure handling both have to be explicit here.
 *
 * Note this makes PDF a download rather than an in-tab preview — an accepted,
 * documented consequence of the transport change, not an oversight.
 */
import fileDownload from 'js-file-download';

/**
 * Save the exported bytes.
 *
 * @param {Blob} data         response body
 * @param {String} reportName must match the server's `getFilename()` exactly —
 *                            this path names the file client-side and ignores
 *                            `Content-Disposition`
 * @param {String} type       export format ('csv' | 'xlsx' | 'pdf', any case);
 *                            lowercased to match `Export::setFileType()`
 */
export function deliverExport(data, reportName, type) {
    fileDownload(data, `${reportName}.${String(type).toLowerCase()}`);
}

/**
 * Report a failed export. Without this the rejection is swallowed: the file is
 * fetched in the background rather than navigated to, so the user clicks Export
 * and concludes the button is broken.
 *
 * @param {String} reportName
 * @param {String} type
 * @param {Error} error
 */
export function notifyExportError(reportName, type, error) {
    const label = String(type).toUpperCase();
    const status = _.get(error, 'response.status', 'unknown');

    console.error(`[export] ${reportName} ${label} failed (${status})`, error);

    toastr.error(
        `Could not export ${reportName} as ${label}. Please try again, or ` +
            'narrow your filters if the report is very large.',
        'Export failed',
    );
}



// WEBPACK FOOTER //
// ./resources/assets/js/plugins/export-delivery.js